import {
  AfterContentInit,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ToasterService } from '../../../../services/core/toaster.service';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { first, finalize } from 'rxjs/operators';
import { CoreService } from '../../../../services/core/core.service';
import { BidDataService } from '../bid.data.service';
import { Router } from '@angular/router';
import { MatStepper } from '@angular/material/stepper';
import { ModuleDataMilestoneService } from '../../../../services/module-data-milestone.service';
import { ReservationPickerComponent } from '../../../shared/reservation-picker/reservation-picker.component';

@Component({
    selector: 'app-create-edit-bid',
    templateUrl: './create-edit-bid.component.html',
    styleUrls: ['./create-edit-bid.component.scss'],
    providers: [
        {
            provide: STEPPER_GLOBAL_OPTIONS,
            useValue: { showError: true },
        },
    ],
    standalone: false
})
export class CreateEditBidComponent implements OnInit {
  public isEdit: boolean = false;
  @Output() onAdd = new EventEmitter<any>(true);
  public submitted = false;
  public isSaving = false;
  public model: any = {};
  public bidForm!: UntypedFormGroup;
  public step: any;
  defaultCustomerId: string = '';
  defaultContactId: string = '';

  defaultDate: Date | null = null;
  defaultTime: Date | null = null;
  isFromCalendar: boolean = false;
  isCellClick: boolean = false; // Add cell click flag

  /** Module id of the Bid module — used for milestone auto-deploy and
   * Number Reservation. */
  private readonly BID_MODULE_ID = 16;

  // Number Reservation — set when the user picks a released number from the
  // "Use a reserved number?" dialog; left null means a brand-new number gets
  // generated, same as before this feature. Create only, not edit.
  public pickedReservation: any = null;

  constructor(
    private dialog: MatDialog,
    private _formBuilder: UntypedFormBuilder,
    public fb: UntypedFormBuilder,
    public toasterService: ToasterService,
    public coreService: CoreService,
    public bidDataService: BidDataService,
    private router: Router,
    private moduleDataMilestoneService: ModuleDataMilestoneService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<CreateEditBidComponent>
  ) {
    this.model = this.data?.model;
    this.isEdit = this.data?.isEdit;
    // These fields are only relevant when user create bid from bid selection pop up of the proposal
    this.defaultCustomerId = this.data?.defaultCustomerId;
    this.defaultContactId = this.data?.defaultContactId;

    // Check if opened from calendar
    this.isFromCalendar = this.data?.isFromCalendar || false;
    this.isCellClick = this.data?.isCellClick || false;

    // Only set default values if opened from calendar
    if (this.isFromCalendar) {
      this.defaultDate = this.data?.selectedCalendarDate;
      this.defaultTime = this.data?.defaultCalenderTime;
    }
  }

  ngOnInit() {
    this.bidForm = new UntypedFormGroup({
      step01: new UntypedFormGroup({
        bidDetail: new UntypedFormControl(null),
      }),
      step02: new UntypedFormGroup({
        scopesStage: new UntypedFormControl(null),
      }),
      step03: new UntypedFormGroup({
        assignment: new UntypedFormControl(null),
      }),
      step04: new UntypedFormGroup({
        jobsiteAddressDetail: new UntypedFormControl(null),
      }),
      step05: new UntypedFormGroup({
        preview: new UntypedFormControl(null),
      }),
    });
  }

  onStepChanged = (event: any, stepper: any) => {
    this.step = { event: event, stepper: stepper };
    console.log('event', this.bidForm.value);
  };

  touchFormControls(
    formControlName: string,
    secondFormControlName: any = null
  ) {
    console.log('formcontrolname', formControlName);
    let bidForm = this.bidForm.get(formControlName);
    console.log('bidformcontrolname', bidForm);
    // @ts-ignore
    bidForm = secondFormControlName
      ? bidForm?.get(secondFormControlName)
      : bidForm;
    bidForm?.markAsTouched();
    bidForm?.updateValueAndValidity({ onlySelf: false, emitEvent: true });
  }

  close = (data: any = '') => {
    this.dialogRef.close(data);
  };

  onSubmit() {
    if (this.bidForm.invalid || this.isSaving) {
      return;
    }

    if (this.isEdit) {
      // process scopes array
      const scopes = this.bidForm?.value?.step02?.scopesStage?.scopes;
      const processedScopes = scopes.filter((scope: any) => {
        if ('is_bid_scope' in scope) {
          if (scope['is_bid_scope']) {
            return true;
          } else {
            return false;
          }
        } else {
          return true;
        }
      });
      if (!processedScopes.length) {
        this.toasterService.showErrorMessage(
          'Please choose atleast one bid scope'
        );
        return;
      }
      this.onUpdate(processedScopes);
      return;
    }
    this.onCreate();
  }

  openReservationPicker(): void {
    const dialogRef = this.dialog.open(ReservationPickerComponent, {
      width: '460px',
      maxWidth: '460px',
      maxHeight: '80vh',
      data: { moduleId: this.BID_MODULE_ID },
    });
    dialogRef.afterClosed().subscribe((result: any) => {
      if (result?.reservationId) {
        this.pickedReservation = result;
      }
    });
  }

  clearReservation(): void {
    this.pickedReservation = null;
  }

  onCreate = () => {
    this.isSaving = true;
    this.bidDataService
      .create(this.getFormData())
      .pipe(
        first(),
        finalize(() => {
          this.isSaving = false;
        })
      )
      .subscribe({
        next: (data) => {
          this.bidForm.reset();
          const newBidId = data?.data?.data?.id;
          if (newBidId) {
            this.autoDeployMilestones(newBidId);
          }
          this.close(data);
          this.toasterService.showSuccessMessage(data?.data.message);
        },
        error: (error) => {
          const message =
            error?.error?.message || 'Unable to create bid. Please try again.';
          this.toasterService.showErrorMessage(message);
        },
      });
  };

  /**
   * After a bid is created, attach the Bid module's auto-deploy milestone template
   * (if one is enabled). Fire-and-forget — failures are logged silently and never
   * block the create flow.
   */
  private autoDeployMilestones(bidId: number) {
    this.moduleDataMilestoneService
      .autoDeploy(this.BID_MODULE_ID, bidId)
      .pipe(first())
      .subscribe({
        next: () => {},
        error: (err) => {
          console.error('Milestone auto-deploy failed for bid', bidId, err);
        },
      });
  }

  onUpdate = (processedScopes: any = []) => {
    this.isSaving = true;
    this.bidDataService
      .update(this.model?.id, this.getFormData(processedScopes))
      .pipe(
        first(),
        finalize(() => {
          this.isSaving = false;
        })
      )
      .subscribe({
        next: (data) => {
          this.bidForm.reset();
          this.close(data);
          this.toasterService.showSuccessMessage(data?.data.message);
        },
        error: (error) => {
          const message =
            error?.error?.message || 'Unable to update bid. Please try again.';
          this.toasterService.showErrorMessage(message);
        },
      });
  };
  goBack(stepper: MatStepper) {
    stepper.previous();
  }

  changedHandler = (event: any) => {};

  getFormData = (processedScopes = null) => {
    const startTime = this.bidForm?.value?.step01?.bidDetail?.startTime;
    console.log('startTime', startTime);
    let data = {
      name: this.bidForm?.value?.step01?.bidDetail?.name,
      building_specifications: this.bidForm?.value?.step01?.bidDetail?.building_specifications,
      customer_id:
        this.bidForm?.value?.step01?.bidDetail?.customer?.customer_id?.id,
      contact_id:
        this.bidForm?.value?.step01?.bidDetail?.customer?.contact_id?.id,
      due_date_at: this.coreService.toMomentDate(
        this.bidForm?.value?.step01?.bidDetail?.due_date_at
      ),
      due_time: startTime ? startTime.toTimeString().split(' ')[0] : '',
      tenant_id: this.bidForm?.value?.step01?.bidDetail?.tenant_id,
      tenant_ids: this.bidForm?.value?.step01?.bidDetail?.tenant_ids || [],

      source_id: this.bidForm?.value?.step02?.scopesStage?.source_id?.id,
      scopes:
        processedScopes && Array.isArray(processedScopes)
          ? this.coreService.arrayObjectId(processedScopes)
          : this.coreService.arrayObjectId(
              this.bidForm?.value?.step02?.scopesStage?.scopes
            ),
      bid_types: this.coreService.arrayObjectId(
        this.bidForm?.value?.step02?.scopesStage?.bid_types
      ),
      probability: this.bidForm?.value?.step02?.scopesStage
        ? this.bidForm?.value?.step02?.scopesStage?.probability
        : '',
      stage_id: this.bidForm?.value?.step02?.scopesStage?.stage_id?.id,
      description: this.bidForm?.value?.step02?.scopesStage?.description,
      project_manager_id:
        this.bidForm?.value?.step03?.assignment?.project_manager_id?.id,
      assigned_to_id:
        this.bidForm?.value?.step03?.assignment?.assigned_to_id?.id,
      estimator_id: this.bidForm?.value?.step03?.assignment?.estimator_id?.id,
      sales_rep_id: this.bidForm?.value?.step03?.assignment?.sales_rep_id?.id,
      secondary_sales_rep_id:
        this.bidForm?.value?.step03?.assignment?.secondary_sales_rep_id?.id,
      jobsite_id:
        this.bidForm?.value?.step04?.jobsiteAddressDetail?.jobsite_id?.id,
      city: this.bidForm?.value?.step04?.jobsiteAddressDetail?.jobsiteAddress
        ?.city,
      address_line_1:
        this.bidForm?.value?.step04?.jobsiteAddressDetail?.jobsiteAddress
          ?.address_line_1,
      state_id:
        this.bidForm?.value?.step04?.jobsiteAddressDetail?.jobsiteAddress
          ?.state_id?.id,
      address_line_2:
        this.bidForm?.value?.step04?.jobsiteAddressDetail?.jobsiteAddress
          ?.address_line_2,
      zipcode:
        this.bidForm?.value?.step04?.jobsiteAddressDetail?.jobsiteAddress
          ?.zipcode,
    };

    if (this.isEdit) {
      delete data['customer_id'];
      delete data['contact_id'];
      return data;
    }
    (data as any).reservation_id = this.pickedReservation?.reservationId ?? null;
    return data;
  };
}

/*assigned_to_id:
  id: 1
name: "Company 01"
  [[Prototype]]: Object
estimator_id:
  id: 1
name: "Company 01"
  [[Prototype]]: Object
sales_rep_id:
  id: 1
name: "Company 01"
  [[Prototype]]: Object*/
